<template>
    <div
        v-loading="loading">
        <el-row>
            <el-col v-if="task"
                    :span="24">
                <el-row>
                    <!-- Card -->
                    <defect-card class="mb-4"
                                 :task="task"
                                 :preview-mode="false"
                                 @photo-click="showGalleryByTask" />

                    <!-- Captures -->
                    <task-captures :task="task"
                                   @capture-photo-click="showGalleryByCapture"
                                   @capture-position-click="goToPosition" />

                    <!-- Fields -->
                    <el-col :span="dependent ? 24 : 13">
                        <el-form :model="editedItem"
                                 label-width="12rem"
                                 :label-position="dependent ? 'top' : 'left'"
                                 size="mini"
                                 @submit.native.prevent>
                            <!-- Name -->
                            <input-field 
                                v-model="editedItem.name"
                                label="Название задачи"
                                placeholder="Введите название"
                                :required="isRequiredField('name')"
                                :disabled="!isEditableField('name')"
                                :errors="errors['name'] || editableFieldsErrorsByName('name')" />

                            <!-- Kind -->
                            <defect-kind-field 
                                v-model="editedItem.kind_defect"
                                :required="isRequiredField('kind_defect')"
                                :disabled="!isEditableField('kind_defect')"
                                :errors="errors['kind_defect'] || editableFieldsErrorsByName('kind_defect')" />

                            <!-- Expired at -->
                            <date-field 
                                v-model="editedItem.expired_at"
                                label="Дата завершения"
                                :required="isRequiredField('expired_at')"
                                :disabled="!isEditableField('expired_at')"
                                :errors="errors['expired_at'] || editableFieldsErrorsByName('expired_at')" />

                            <!-- CC -->
                            <user-field
                                v-model="editedItem.contractor"
                                label="Подрядчик"
                                :required="isRequiredField('worker_id') || isRequiredField('new_contractor_id')"
                                :disabled="!isEditableField('worker_id') && !isEditableField('new_contractor_id')"
                                :errors="errors['worker_id'] || editableFieldsErrorsByName('worker_id') || errors['new_contractor_id'] || editableFieldsErrorsByName('new_contractor_id')"
                                is-contractor
                                with-blocked>
                                <template #extra-fields>
                                    <!-- CC history -->
                                    <trashed-members v-if="trashedMembers.contractors?.length"
                                                     :members="trashedMembers.contractors"
                                                     placement="right-start" />
                                </template>
                            </user-field>

                            <!-- E1 -->
                            <user-field
                                v-model="editedItem.engineer"
                                label="Инженер СК Г/П"
                                :required="isRequiredField('engineer_id') || isRequiredField('new_engineer_first_stage_id')"
                                :disabled="!isEditableField('engineer_id') && !isEditableField('new_engineer_first_stage_id')"
                                :errors="errors['engineer_id'] || editableFieldsErrorsByName('engineer_id') || errors['new_engineer_first_stage_id'] || editableFieldsErrorsByName('new_engineer_first_stage_id')"
                                is-engineer
                                with-blocked>
                                <template #extra-fields>
                                    <!-- E1 history -->
                                    <trashed-members v-if="trashedMembers.engineers?.length"
                                                     :members="trashedMembers.engineers"
                                                     placement="right-start" />
                                </template>  
                            </user-field>

                            <!-- GE -->
                            <user-field
                                v-if="isEditableField('new_engineer_second_stage_id')"
                                v-model="editedItem.new_engineer_second_stage_id"
                                label="Инженер СКЗ"
                                :errors="errors['new_engineer_second_stage_id'] || editableFieldsErrorsByName('new_engineer_second_stage_id')"
                                is-general-engineer
                                as-key
                                with-blocked>
                                <template #extra-fields>
                                    <!-- GE history -->
                                    <trashed-members v-if="trashedMembers.general_engineers?.length"
                                                     :members="trashedMembers.general_engineers"
                                                     placement="right-start" />
                                </template>
                            </user-field>    

                            <!-- Inspector -->
                            <user-field 
                                v-model="editedItem.inspectors"
                                label="Контроль"
                                :required="isRequiredField('general_contractor_ids')"
                                :disabled="!isEditableField('general_contractor_ids')"
                                :errors="errors['general_contractor_ids'] || editableFieldsErrorsByName('general_contractor_ids')"
                                is-inspector
                                multiple
                                with-blocked />

                            <!-- AST contract -->
                            <contract-field
                                v-if="isEditableField('ast_contract_id') || contractSelection"
                                v-model="contractSelection"
                                label="Договор (будет указан в ЭДО)"
                                :ast-contract="contract"
                                :contractor-id="contractorId"
                                :required="isRequiredField('ast_contract_id')"
                                :disabled="!isEditableField('ast_contract_id')"
                                :errors="errors['ast_contract_id'] || editableFieldsErrorsByName('ast_contract_id')"
                                creatable
                                with-works />

                            <!-- Place -->
                            <place-field 
                                v-model="editedItem.place"
                                :required="isRequiredField('house_id') || isRequiredField('floor_id')"
                                :disabled="!isEditableField('house_id') || !isEditableField('floor_id')"
                                :errors="errors['house_id'] || errors['floor_id'] || editableFieldsErrorsByName('house_id') || editableFieldsErrorsByName('floor_id')"
                                label="Относится к"
                                with-at-project />

                            <!-- Work type description -->
                            <input-field
                                v-model="editedItem.work_type_description"
                                label="Вид работ (в ходе которых был обнаружен дефект)"
                                type="textarea"
                                :required="isRequiredField('work_type_description')"
                                :disabled="!isEditableField('work_type_description')"
                                :errors="errors['work_type_description'] || editableFieldsErrorsByName('work_type_description')" />

                            <!-- Construction position -->
                            <construction-position-field
                                v-model="editedItem.construction_position"
                                :required="isRequiredField('construction_position')"
                                :disabled="!isEditableField('construction_position')"
                                :errors="errors['construction_position'] || editableFieldsErrorsByName('construction_position')" />

                            <!-- Construction mark -->
                            <input-field 
                                v-model="editedItem.construction_mark"
                                :label="labels.construction_mark"
                                placeholder="Введите значение"
                                :required="isRequiredField('construction_mark')"
                                :disabled="!isEditableField('construction_mark')"
                                :errors="errors['construction_mark'] || editableFieldsErrorsByName('construction_mark')" />

                            <!-- Construction axis -->
                            <input-field 
                                v-model="editedItem.construction_axis"
                                :label="labels.construction_axis"
                                placeholder="Введите значение"
                                :required="isRequiredField('construction_axis')"
                                :disabled="!isEditableField('construction_axis')"
                                :errors="errors['construction_axis'] || editableFieldsErrorsByName('construction_axis')" />

                            <!-- Standard -->
                            <building-standard-field 
                                v-model="editedItem.defect_types"
                                :required="isRequiredField('defect_types')"
                                :disabled="!isEditableField('defect_types')"
                                :errors="errors['defect_types'] || editableFieldsErrorsByName('defect_types')" />

                            <!-- Standard (by input) -->
                            <input-field 
                                v-model="editedItem.custom_defect_type"
                                label="Нормативный документ (от пользователя)"
                                placeholder="Введите название дефекта"
                                :required="isRequiredField('custom_defect_type')"
                                :disabled="!isEditableField('custom_defect_type')"
                                :errors="errors['custom_defect_type'] || editableFieldsErrorsByName('custom_defect_type')" />

                            <!-- Description -->
                            <input-field v-model="editedItem.description"
                                         label="Выявленные нарушения и рекомендации по устранению"
                                         placeholder="Введите текст описания"
                                         type="textarea"
                                         :required="isRequiredField('description')"
                                         :disabled="!isEditableField('description')"
                                         :errors="errors['description'] || editableFieldsErrorsByName('description')" />

                            <!-- Comment (initiator) -->
                            <input-field v-model="editedItem.comment_initiator"
                                         label="Комментарий к добавлению дефекта"
                                         placeholder="Введите текст комментария"
                                         type="textarea"
                                         :required="isRequiredField('comment_initiator')"
                                         :disabled="!isEditableField('comment_initiator')"
                                         :errors="errors['comment_initiator'] || editableFieldsErrorsByName('comment_initiator')" />

                            <!-- Worker comment -->
                            <input-field v-model="editedItem.comment_worker"
                                         label="Комментарий подрядчика"
                                         type="textarea"
                                         :required="isRequiredField('comment_worker')"
                                         :errors="errors['comment_worker'] || editableFieldsErrorsByName('comment_worker')"
                                         disabled />

                            <!-- Repeated -->
                            <switch-field v-model="editedItem.repeated"
                                          label="Повторное предписание"
                                          :required="isRequiredField('repeated')"
                                          :disabled="!isEditableField('repeated')"
                                          :errors="errors['repeated'] || editableFieldsErrorsByName('repeated')" />

                            <fieldable 
                                v-if="isEditableField('rejection_ast_work_ids') || rejectionContract.ast_work_ids?.size"
                                label="Заблокировать создание приемок работ:"
                                name="rejection_ast_work_ids"
                                :errors="errors['rejection_ast_work_ids']"
                                :required="isRequiredField('rejection_ast_work_ids')">
                                <div class="space-y-0.25">
                                    <div class="f-y-center gap-0.5">
                                        <el-switch v-model="blockedAcceptance" 
                                                   :disabled="!isEditableField('rejection_ast_work_ids')" />
                                        <span class="text-gray-500 fs-0.75 lh-0.75">
                                            Приемка работ будет заблокирована, пока подрядчик не устранит предписание 
                                        </span>
                                    </div>
                                    <contract-field 
                                        v-if="blockedAcceptance"
                                        v-model="rejectionContract"
                                        :contractor-id="contractorId"
                                        with-works-selection-by-contracts
                                        with-contracts-selection
                                        independent
                                        rejectionable
                                        :readonly="!isEditableField('rejection_ast_work_ids')" />
                                </div>
                            </fieldable>

                            <!-- Photos -->
                            <file-field v-if="editablePhoto"
                                        v-model="photos"
                                        label="Фотография"
                                        :modal="!dependent"
                                        :errors="errors['task_image_ids'] || errors['image_defining_point_id'] || errors['image'] || errors['images.0.image'] || editableFieldsErrorsByName('task_image_ids') || editableFieldsErrorsByName('image_defining_point_id')"
                                        with-camera />

                            <div v-if="connectedTasks.length"
                                 class="f">
                                <div class="mb-5">
                                    <span class="text-size--body text-color--label pr-3"
                                          style="display: inline-block; width: 140px; box-sizing: border-box">
                                        Связанные задачи:
                                    </span>
                                </div>
                                <div>
                                    <div class="f-col">
                                        <div v-for="order in connectedTasks"
                                             :key="order.id"
                                             class="mb-1">
                                            <router-link class="text-size--body text-color--primary text-decoration--none"
                                                         :to="{ name:'project.task', params:{ taskId: order.task_object_id } }">
                                                {{ getNested(order, 'task_object.name') }}
                                            </router-link>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Buttons -->
                            <div class="cols-minmax-min space-x-1">
                                <!-- Confirm -->
                                <el-button type="primary"
                                           :disabled="!hasRequiredFields && !hasEditableFields"
                                           @click="update">
                                    Сохранить
                                </el-button>

                                <!-- Actions (dependent) -->
                                <task-actions v-if="dependent"
                                              :task="task"
                                              :actions="allowedActions"
                                              as-dropdown
                                              @do-with-data="act" />
                            </div>
                        </el-form>
                    </el-col>
                </el-row>
            </el-col>
        </el-row>

        <!-- Gallery -->
        <task-photos-gallery v-if="shouldDisplayGallery"
                             :media="media"
                             :show-gallery="shouldDisplayGallery"
                             :show-caption="shouldDisplayCaption"
                             :position="galleryIndex"
                             editable
                             @on-close-gallery="hideGallery"
                             @image-select="selectGalleryImage" />

        <!-- Actions (default) -->
        <portal v-if="!dependent"
                to="allowed-actions">
            <task-actions 
                :task="task"
                :actions="allowedActions"
                @do-with-data="act" />
        </portal>
    </div>
</template>
<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'
import { formatForServer, parseAt } from '@/utils/date'
import { startOfDay } from 'date-fns'

import { urlToFile } from '@/utils/browser'
import { fromError, flatCollectionError } from '@/utils/common'
import { then } from '@/utils/immutable'

import { getPlanIdFromPosition, getPointIdFromPosition, getPhotoIdFromPosition } from '@/models/projects'
import { 
  getInspectorUsers, getContractorUser, 
  getWorkerComment, getCaptures, 
  getMembersForSelection, 
  toImageUpdation, 
  getEngineerUser,
  getAstContract,
  getGeneralEngineerUser,
  getTrashedMembers,
  getTaskRejections
} from '@/models/tasks'
import {
  toConstructionPosition,
  fromConstructionPosition
} from '@/models/task-trash'

import { loadable } from '@/store/connectors'

import Fieldable from '@/components/fields/Fieldable.vue'
import InputField from '@/components/fields/InputField'
import TaskActions from '@/components/tasks/TaskActions'
import DateField from '@/components/fields/DateField'
import UserField from '@/components/fields/UserField'
import BuildingStandardField from '@/components/fields/BuildingStandardField'
import DefectKindField from '@/components/fields/DefectKindField'
import PlaceField from '@/components/fields/PlaceField'
import FileField from '@/components/fields/FileField'
import SwitchField from '@/components/fields/SwitchField.vue'
import ContractField from '@/components/fields/ContractField.vue'
import ConstructionPositionField from '@/components/fields/ConstructionPositionField'

import DefectCard from '@/components/task/DefectCard';
import TaskPhotosGallery from '@/components/task/TaskPhotosGallery'
import TaskCaptures from '@/components/task/TaskCaptures'

import TrashedMembers from '@/components/popovers/TrashedMembers'

const labels = {
  construction_position: 'Местонахождение',
  construction_mark: 'Строительная отметка',
  construction_axis: 'В осях',

  showHistory: 'Показать историю'
}

export default {
  name: 'TaskTypeDefectsAndViolations',
  components: {
    Fieldable,
    DefectCard,
    TaskActions,
    DateField,
    BuildingStandardField,
    DefectKindField,
    InputField,
    UserField,
    PlaceField,
    FileField,
    SwitchField,
    ContractField,
    ConstructionPositionField,
    
    TaskPhotosGallery,
    TaskCaptures,

    TrashedMembers
  },
  mixins: [
    loadable({ on: 'tasks', name: 'updateTaskWithError' }),
    loadable({ on: 'tasks', name: 'storeDefectImages' })
  ],
  props: {
    task: { type: Object, required: true },
    allowedActions: { type: Array, default: () => [] },
    dependent: { type: Boolean, default: false }
  },
  data() {
    return {
      initialItem: {},
      editedItem: {
        contractor: null,
        engineer: null,
        expired_at: null,
        inspectors: [],
        place: null,
        defect_types: [],
        custom_defect_type: null,
        description: null,
        name: '',
        kind_defect: '',
        comment_worker: '',
        work_type_description: null,
        repeated: false,
        construction_position: '',
        construction_mark: null,
        construction_axis: null
      },
      objectsBuildingOrderConnection: [],
      objectsAcceptanceOfWorkConnection: [],
      errors: {},
      oldLoading: false,

      photos: [],

      contract: null,
      contractSelection: null,
      rejectionContract: {
        ast_contract_ids: new Map(),
        ast_work_ids: new Map()
      },
      blockedAcceptance: false,

      trashedMembers: {},

      // gallery
      shouldDisplayGallery: false,
      shouldDisplayCaption: false,
      galleryIndex: 0,
      media: []
    }
  },
  computed: {
    ...mapState('tasks', { currentTask: state => state.task }),
    ...mapGetters('tasks', [
      'defectKinds', 
      'hasRequiredFields', 'hasEditableFields', 'isEditableField', 'isRequiredField', 'hasRequiredFieldValue', 'getRequiredFieldValues', 'editableFieldsErrorsByName'
    ]),
    ...mapGetters('project', ['project']),

    labels() {
      return {
        ...labels
      }
    },

    connectedTasks() {
      return [...this.objectsBuildingOrderConnection, ...this.objectsAcceptanceOfWorkConnection];
    },

    captures() {
      return getCaptures(this.task, { withLabels: true, withPrefixes: true })
    },

    loading() {
      return this.oldLoading || this.updateTaskWithErrorLoading || this.storeDefectImagesLoading
    },

    editablePhoto() {
      return this.captures.length <= 1 && (this.isEditableField('task_image_ids') || this.isEditableField('image_defining_point_id')) 
    },

    contractorId() {
      return then(this.editedItem.contractor, x => x._isUser && x.id)
    }
  },
  watch: {
    currentTask: {
      immediate: true,
      handler: function (x) {
        this.initializeItem(x);
      }
    }
  },
  methods: {
    ...mapMutations('dialog', {showDialog: 'SHOW_DIALOG'}),
    ...mapActions('tasks', ['storeDefectImages', 'makeTaskAction', 'updateTaskWithError']),
    
    showGalleryByTask() {
      this.showGallery(0)
    },

    showGalleryByCapture(capture) {
      const i = this.captures.findIndex(({ id }) => id === capture.id)

      i !== -1 && this.showGallery(i)
    },

    showGallery(i) {
      this.media = this.captures.map(({ id, thumb, object_id, object_type }) => ({
        id,
        thumb,
        src: thumb,
        object_id,
        object_type
      }))

      this.shouldDisplayGallery = true
      this.shouldDisplayCaption = false
      this.galleryIndex = i
    },

    hideGallery() {
      this.shouldDisplayGallery = false
      this.shouldDisplayCaption = false
    },

    selectGalleryImage(image) {
      const i = this.media.findIndex(({ id }) => id === image?.id)

      this.galleryIndex = i
    },

    goToPosition(position) {
      const planId = getPlanIdFromPosition(position)
      const pointId = getPointIdFromPosition(position)
      const photoId = getPhotoIdFromPosition(position)

      planId && pointId && !photoId && this.$router.push({ name: 'project.plan', params: { planId, selection: [pointId] } })
      photoId && this.$router.push({ name: 'project.photo', params: { photoId } })
    },

    prepareData() {
      const { ast_contract_id } = this.contractSelection || {}

      const data = {
        ...this.prepareEditedItem({
          contractor: {
            key: this.isEditableField('new_contractor_id') ? 'new_contractor_id' : 'worker_id',
            comparator: (a, b) => a?.id === b?.id,
            to: x => x.id 
          },
          engineer: {
            key: this.isEditableField('new_engineer_first_stage_id') ? 'new_engineer_first_stage_id' : 'engineer_id',
            comparator: (a, b) => a?.id === b?.id,
            to: x => x.id
          },
          place: {
            unwrap: true,
            to: x => ({
              house_id: x?.house?.id || null,
              floor_id: x?.floor?.id || null
            })
          },
          construction_position: {
            to: x => toConstructionPosition(x)
          },
          inspectors: {
            key: 'general_contractor_ids',
            to: x => x.map(({ id }) => id)
          },
          expired_at: {
            to: x => formatForServer(startOfDay(x), { zoned: false })
          }
        }),

        ast_contract_id,
        ...this.isEditableField('rejection_ast_contract_ids') && { rejection_ast_contract_ids: this.blockedAcceptance ? [...this.rejectionContract.ast_contract_ids.keys()] : [] },
        ...this.isEditableField('rejection_ast_work_ids') && { rejection_ast_work_ids: this.blockedAcceptance ? [...this.rejectionContract.ast_work_ids.keys()] : [] }
      }

      return data
    },
    
    prepareEditedItem(replaceableFields = {}) {
      return Object.entries(this.editedItem).reduce((r, [k, v]) => {
        const replacer = replaceableFields[k] || {}
        const comparator = replacer.comparator || ((a, b) => a === b)
        
        if (!comparator(v, this.initialItem[k])) {
          if (replacer.unwrap && replacer.to)
            return { ...r, ...replacer.to(v)} 

          return {
            ...r,
            [replaceableFields[k]?.key || k]: replaceableFields[k]?.to ? replaceableFields[k]?.to(v) : v
          }
        }
        
        return r
      }, {})
    },

    update() {
      const updatablePhotos = this.photos.filter(({ id }) => id)
      const creatablePhotos = this.photos.filter(({ id }) => !id)

      this.storeDefectImages({ 
        task: this.task, 
        images: creatablePhotos.map(({ file }) => file) 
      })
        .then(ids => ({
          ...this.prepareData(),
          ...this.editablePhoto 
            && toImageUpdation(this.task, { updatable: updatablePhotos.map(({ id }) => id), creatable: ids })
        }))
        .then(payload => this.updateTaskWithError({
          taskId: this.task.id,
          taskType: this.task.type,
          payload
        }))
        .then(dialogs.success.bind(this, { message: 'Задача обновлена' }))
        .then(() => this.errors = {})
        .catch(({ messages }) => this.errors = {
          ...messages,
          'general_contractor_ids': flatCollectionError(messages, 'general_contractor_ids')
        })
    },

    // NOT REVIEWED

    initializeItem(task) {
      const { data } = task

      this.members = getMembersForSelection(task)

      this.editedItem.inspectors = getInspectorUsers(task).filter(is).map(({ id }) => ({ id, organization: {}, _isUser: true }))

      this.editedItem.contractor = then(getContractorUser(task), x => ({ ...x, _isUser: true }))
      this.editedItem.engineer = then(getEngineerUser(task), x => ({ ...x, _isUser: true }))
      this.editedItem.new_engineer_second_stage_id = getGeneralEngineerUser(task)?.id

      this.editedItem.name = this.getNested(task, 'name', '');
      this.editedItem.expired_at = parseAt(this.getNested(task, 'expired_at', null), { zoned: true });
      this.editedItem.description = this.getNested(task, 'description', null);
      this.editedItem.defect_types = task.data.defect_types.map(({ id }) => id);
      this.editedItem.custom_defect_type = task.data.custom_defect_type
      this.editedItem.comment_worker = this.getNested(task, 'data.comment_worker', '');
      this.editedItem.comment_initiator = data?.comment_initiator
      this.editedItem.repeated = data?.repeated || false

      this.editedItem.object_id = this.editedItem.house_id || this.editedItem.floor_id || this.project.id

      this.editedItem.kind_defect = this.getNested(task, 'data.kind_defect');

      this.editedItem.work_type_description = task.data?.work_type_description

      this.editedItem.construction_position = fromConstructionPosition(data.construction_position)

      this.editedItem.construction_mark = data.construction_mark
      this.editedItem.construction_axis = data.construction_axis
      this.objectsBuildingOrderConnection = this.getNested(task, 'objects_building_order_connection', []);
      this.objectsAcceptanceOfWorkConnection = this.getNested(task, 'objects_acceptance_of_work_connection', []);

      const houseId = this.getNested(task, 'data.project_point_position.house.id', null)
        || this.getNested(task, 'data.acceptance_work_object.project_object_structure.house.id', null);
      const floorId = this.getNested(task, 'data.project_point_position.floor.id', null)
        || this.getNested(task, 'data.acceptance_work_object.project_object_structure.floor.id', null);

      this.editedItem.place = ((houseId || floorId) && { house: { id: houseId } , floor: { id: floorId } }) || null

      this.editablePhoto && 
        this.captures.length === 1 && 
        urlToFile(this.captures[0].image, { name: this.captures[0].name })
          .then(file => this.photos = [{
            id: this.captures[0].object_id,
            file,
            name: file.name
          }])

      this.contract = then(getAstContract(task), x => x) ?? null
      this.contractSelection = then(getAstContract(task), x => ({ ast_contract_id: x.id })) ?? null
      this.rejectionContract = then(
        getTaskRejections(task), 
        x => ({ 
          ast_contract_ids: new Map(x?.[0]?.ast_works?.map(({ast_contract}) => [ast_contract.id, true]) || []), 
          ast_work_ids: new Map(x?.[0]?.ast_works?.map(({id}) => [id, true]) || []) 
        })
      )

      if (this.rejectionContract?.ast_work_ids?.size) this.blockedAcceptance = true

      this.trashedMembers = getTrashedMembers(task) || {}

      this.initialItem = { ...this.editedItem }
    },

    act(action) {
      const { name, allowed_fields = [] } = action

      this.errors = {}

      const go = (data = {}) => this.makeTaskAction({
        taskId: this.task.id,
        taskType: this.task.type,
        payload: {
          action: name,
          data
        }
      }).catch(fromError)

      allowed_fields.length && this.showDialog({
        dialogName: 'task-defects-and-violations-dialog',
        payload: {
          action,
          contractor: getContractorUser(this.task),
          comment: getWorkerComment(this.task)
        },
        action: go
      })

      !allowed_fields.length && (() => {
        this.oldLoading = true;

        go()
          .then(() => {
            this.errors = {}
            !is['send_for_revision_second_stage'].includes(name) && this.$router.push({ name: 'project.tasks.defects' })
          })
          .catch(({ messages }) => {
            this.errors = messages || {}
          })
          .finally(() => this.oldLoading = false)
      })()
    },

    resetForm(formName) {
      this.$refs[formName].resetFields();
      this.$refs.multipleTable.clearSelection();
    }
  }
};
</script>
